import { useBaseStore } from '@/store';
import { getInitials } from '@/utils/stringFormatter';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import ProfileMenu from './components/ProfileMenu';
import { Separator } from '../ui/separator';
import { Store } from '@/store/type';
import { Link, useLocation } from 'react-router-dom';
import TextLogo from '@/assets/icons/text_logo.png';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { getRouteMeta } from '@/utils/breadcrumbUtils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

const stateSelector = (state: Store) => ({
  isSidenavOpen: state.appConfigSlice.state.isSidenavOpen,
  setSidenavOpen: state.appConfigSlice.actions.setSidenavOpen,
  user: state.authSlice.state.user,
  profile: state.authSlice.state.profile,
  isMobile: state.appConfigSlice.state.isMobile,
});

const Header = () => {
  const { isSidenavOpen, setSidenavOpen, user, isMobile } =
    useBaseStore(stateSelector);
  const location = useLocation();
  const routeMeta = getRouteMeta(location.pathname);

  return (
    <header className="flex items-center justify-between w-full px-4 h-14 bg-background border-b border-border text-foreground transition-all duration-200">
      {/* Left Section: Toggle & Dynamic Breadcrumb */}
      <div className="flex items-center gap-3">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidenavOpen(!isSidenavOpen)}
                className="hidden md:flex size-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
                aria-label="Toggle Navigation Sidebar"
              >
                {isSidenavOpen ? (
                  <PanelLeftClose className="size-4" />
                ) : (
                  <PanelLeftOpen className="size-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {isSidenavOpen ? 'Recolher menu' : 'Expandir menu'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Link to="/" className="md:hidden block flex-shrink-0">
          <img src={TextLogo} alt="Logo" className="h-5" />
        </Link>

        <Separator
          orientation="vertical"
          className="hidden md:block h-4 bg-border"
        />

        {/* Dynamic Breadcrumbs */}
        {!isMobile && (
          <div className="hidden sm:block">
            <Breadcrumb>
              <BreadcrumbList>
                {routeMeta.breadcrumbs.map((item, index) => {
                  const isLast = index === routeMeta.breadcrumbs.length - 1;
                  return (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1.5"
                    >
                      {index > 0 && <BreadcrumbSeparator />}
                      <BreadcrumbItem>
                        {isLast || !item.to ? (
                          <BreadcrumbPage className="text-sm font-semibold text-foreground">
                            {item.label}
                          </BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link
                              to={item.to}
                              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {item.label}
                            </Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </span>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        )}
      </div>

      {/* Right Section: User Profile Menu */}
      <div className="flex items-center gap-3">
        <ProfileMenu
          trigger={
            <button className="flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1">
              <Avatar className="size-7 rounded-full border border-border">
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                  {getInitials(user || '')}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline-block text-sm font-medium text-foreground truncate max-w-[140px]">
                {user}
              </span>
            </button>
          }
        />
      </div>
    </header>
  );
};

export default Header;
